import { removeNumberFormatting } from './math.helpers';

// ---------------------------------------------------------------------------
// DEV-3298: waste / spill column scale interpretation.
//
// CSV "Spill" is treated as spillfaktor (1 = no spill, 2 = 100% spill) and
// converted to the 0–100 percentage the nodon WASTE_PERCENT override expects.
// ---------------------------------------------------------------------------

/**
 * Waste / spill column scale — always spillfaktor, mapped to 0–100% by
 * {@link wasteValueToPercent}: `1` = no spill, `2` = 100% spill (`1.05` = 5%).
 */
export type WasteScale = '1-2';

/**
 * When a waste column has at least one numeric body value, import uses
 * spillfaktor (`1-2`). Returns `undefined` when every cell is empty.
 */
export const detectWasteScale = (
  values: readonly (number | null | undefined)[],
): WasteScale | undefined => {
  const hasValue = values.some(
    (v) => typeof v === 'number' && Number.isFinite(v),
  );
  return hasValue ? '1-2' : undefined;
};

/** Convert one spillfaktor value to a 0–100 spill percentage. */
export const wasteValueToPercent = (value: number): number => (value - 1) * 100;

/**
 * Parse a raw waste/spill CSV cell to its spillfaktor number. Tolerates a
 * trailing '%' and locale formatting (',' decimals, thousand separators).
 * Unlike a plain numeric check, whole-number cells like `1` or `2` parse fine.
 */
export const parseWasteCell = (raw: string): number | undefined => {
  const trimmed = raw.replace('%', '').trim();
  if (trimmed.length === 0) return undefined;
  const value = Number(removeNumberFormatting(trimmed));
  return Number.isFinite(value) ? value : undefined;
};

/** Parse a raw waste/spill CSV cell to its 0–100 spill percentage. */
export const wasteCellToPercent = (raw: string): number | undefined => {
  const value = parseWasteCell(raw);
  return value === undefined ? undefined : wasteValueToPercent(value);
};
